import { CheckCircleOutlined, WarningAmberOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Popover,
  PopoverProps,
  Typography,
  useTheme,
} from '@mui/material';
import { ReactNode, memo, useCallback, useMemo } from 'react';

/**
 * Props for the ConfirmationPopover component
 */
interface ConfirmationPopoverProps {
  /** Whether the popover is open */
  open: boolean;
  /** The HTML element to anchor the popover to */
  anchorEl: HTMLElement | null;
  /** Callback fired when the popover should be closed */
  onClose: () => void;
  /** Callback fired when the user confirms the action */
  onConfirm: () => void;
  /** The title text displayed at the top of the popover */
  title?: string;
  /** The confirmation message content (can be string or React element) */
  message?: string | ReactNode;
  /** Text for the confirm button */
  confirmText?: string;
  /** Text for the cancel button */
  cancelText?: string;
  /** Whether the confirm action is currently loading */
  isLoading?: boolean;
  /** Visual variant that affects the icon and styling */
  variant?: 'warning' | 'info';
  /** Custom anchor origin for the popover positioning */
  anchorOrigin?: PopoverProps['anchorOrigin'];
  /** Custom transform origin for the popover positioning */
  transformOrigin?: PopoverProps['transformOrigin'];
}

const ConfirmationPopover = memo(
  ({
    open,
    anchorEl,
    onClose,
    onConfirm,
    title = 'Confirm Action',
    message = 'Are you sure you want to proceed?',
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    isLoading = false,
    variant = 'warning',
    anchorOrigin = {
      vertical: 'top',
      horizontal: 'center',
    },
    transformOrigin = {
      vertical: 'bottom',
      horizontal: 'center',
    },
  }: ConfirmationPopoverProps) => {
    const theme = useTheme();

    const getIcon = useMemo(() => {
      switch (variant) {
        case 'warning':
          return (
            <WarningAmberOutlined
              sx={{
                color: theme.palette.warning.main,
                height: '20px',
                width: '20px',
              }}
            />
          );
        case 'info':
          return (
            <CheckCircleOutlined
              sx={{
                color: theme.palette.info.main,
                height: '20px',
                width: '20px',
              }}
            />
          );
        default:
          return null;
      }
    }, [variant, theme.palette.warning.main, theme.palette.info.main]);

    const handleConfirm = useCallback(() => {
      onConfirm();
      if (!isLoading) {
        onClose();
      }
    }, [onConfirm, onClose, isLoading]);

    return (
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '8px',
            boxShadow: theme.shadows[8],
            border: `1px solid ${theme.palette.divider}`,
            maxWidth: '320px',
          },
        }}
      >
        <Box
          sx={{
            p: 2.4,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {/* Header with icon and title */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {getIcon}
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              {title}
            </Typography>
          </Box>

          {/* Message */}
          <Box>
            {typeof message === 'string' ? (
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  lineHeight: 1.4,
                }}
              >
                {message}
              </Typography>
            ) : (
              message
            )}
          </Box>

          {/* Action buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              justifyContent: 'flex-end',
              mt: 1,
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={onClose}
              disabled={isLoading}
              sx={{
                minWidth: '70px',
              }}
            >
              {cancelText}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleConfirm}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{
                minWidth: '80px',
              }}
            >
              {confirmText}
            </Button>
          </Box>
        </Box>
      </Popover>
    );
  }
);

export default ConfirmationPopover;
